import { Card, CardContent, Link, Typography } from '@material-ui/core';
import { Inline, Stack } from '@superdispatch/ui';
import { MouseEvent, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { isWebView } from 'shared/constants/AppTypeConstants';
import { MobileAppBridge } from 'shared/data/MobileAppBridge';
import { trackEvent } from 'shared/helpers/Analytics';
import { useCustomerSupport } from 'shared/helpers/CustomerSupport';
import { filterSearchParams, useQuery } from 'shared/helpers/RoutingHelpers';
import { openExternalURL } from 'shared/helpers/URLHelpers';
import { useErrorSnackbar } from 'shared/hooks/useErrorSnackbar';
import { autoCloseWebView } from 'shared/helpers/WebViewHelpers';
import { DocumentTitle } from 'shared/layout/DocumentTitle';
import {
  useBillingAddress,
  useEstimateTaxPrice,
  useSubscription,
} from 'shared/modules/subscription/SubscriptionAPI';
import { useSubscriptionCelebrationState } from 'shared/modules/subscription/core/useSubscriptionCelebrationState';
import { useShowCelebrationNotification } from 'shared/modules/subscription/core/useShowCelebrationNotification';
import {
  BillingLocationState,
  SubscriptionEditBillingAddressDrawer,
} from 'shared/modules/subscription/SubscriptionEditBillingAddressDrawer';
import { SubscriptionReactivationDrawer } from 'shared/modules/subscription/SubscriptionReactivationDrawer';
import {
  LocationState,
  SubscriptionSalesTaxDrawer,
} from 'shared/modules/subscription/SubscriptionSalesTaxDrawer';
import {
  SubscriptionUpdateCardDetailsDrawer,
  SubscriptionUpdateCardDetailsDrawerProps,
} from 'shared/modules/subscription/SubscriptionUpdateCardDetailsDrawer';
import { useCarrierSettings } from 'shared/settings/CarrierSettingsAPI';
import { SettingsPageLayout } from './core/SettingsPageLayout';
import { SubscriptionCelebration } from './seat-based-subscription/SubscriptionCelebration';
import { SubscriptionDetailsCard } from './seat-based-subscription/SubscriptionDetailsCard';
import { useFlag } from 'shared/settings/FeatureToggles';
import { SubscriptionContactDrawer } from './seat-based-subscription/SubscriptionContactDrawer';
import { SubscriptionPaymentForm } from './seat-based-subscription/SubscriptionPaymentForm';
import { SubscriptionPaymentAlerts } from './seat-based-subscription/SubscriptionPaymentAlerts';
import { SubscriptionPastDueDrawer } from './seat-based-subscription/SubscriptionPastDueDrawer';
import {
  PastDueCardDetailsLocationState,
  SubscriptionPastDueCardDetailsDrawer,
} from './seat-based-subscription/SubscriptionPastDueCardDetailsDrawer';
import { Skeleton } from '@material-ui/lab';
import { Box } from '@superdispatch/ui-lab';
import { trackSubscriptionEvent } from 'shared/modules/subscription/data/SubscriptionAnalytics';

interface SubscriptionUpdateCardDetailsDrawerRouteProps extends Omit<
  SubscriptionUpdateCardDetailsDrawerProps,
  'open' | 'onClose'
> {}

function SubscriptionUpdateCardDetailsDrawerRoute(
  props: SubscriptionUpdateCardDetailsDrawerRouteProps,
) {
  const navigate = useNavigate();
  const { action } = useParams();
  const isEditCardDetailsDrawerOpen = action === 'update_payment_info';

  return (
    <SubscriptionUpdateCardDetailsDrawer
      {...props}
      open={isEditCardDetailsDrawerOpen}
      onClose={() => {
        navigate('/settings/subscription', { replace: true });
      }}
      onSubmitSuccess={() => {
        props.onSubmitSuccess?.();
      }}
    />
  );
}

interface SubscriptionUpgradePlanDrawerRouteProps {
  refetch: () => void;
  backUrl?: string;
}

function SubscriptionUpgradePlanDrawerRoute({
  refetch,
  backUrl,
}: SubscriptionUpgradePlanDrawerRouteProps) {
  const [{ utm_medium, driver_guid, back_url }] = useQuery();

  const effectiveBackUrl = backUrl || back_url;
  const navigate = useNavigate();
  const location = useLocation();
  const { action } = useParams();
  const isUpgradePlanDrawerOpen = action === 'upgrade_plan';
  const { setBannerStatus } = useSubscriptionCelebrationState();
  const { showCelebrationNotification } = useShowCelebrationNotification();

  const state = location.state as LocationState | undefined;

  if (state && isUpgradePlanDrawerOpen) {
    const { billingAddress, cardDetails, token, vaultToken, seatsCount } =
      state;

    return (
      <SubscriptionSalesTaxDrawer
        seatsCount={seatsCount}
        open={isUpgradePlanDrawerOpen}
        token={token}
        vaultToken={vaultToken}
        billingAddress={billingAddress}
        cardDetails={cardDetails}
        onClose={() => {
          navigate(
            {
              pathname: '/settings/subscription',
              search: filterSearchParams({
                utm_medium,
                driver_guid,
                back_url: effectiveBackUrl,
              }),
            },
            { replace: true, state: undefined },
          );
        }}
        onSubmitSuccess={() => {
          refetch();
          setBannerStatus('unseen');
          showCelebrationNotification();
          autoCloseWebView();

          if (effectiveBackUrl && !isWebView) {
            openExternalURL(effectiveBackUrl);
          }
        }}
      />
    );
  }

  return null;
}

function LoadingCard() {
  return (
    <Card aria-label="Plan">
      <CardContent>
        <Stack space="small">
          <Inline verticalAlign="bottom">
            <Typography display="inline" variant="h3">
              <Skeleton width={200} />
            </Typography>
          </Inline>
          <Stack space={['small', 'xsmall']}>
            <Skeleton width={240} />
            <Skeleton />
            <Skeleton />
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}

interface EditBillingAddressProps {
  open: boolean;
}

function EditBillingAddress({ open }: EditBillingAddressProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { refetch } = useBillingAddress();

  const state = location.state as BillingLocationState | undefined;

  return (
    <SubscriptionEditBillingAddressDrawer
      open={open}
      enableReactivation={state?.enableReactivation}
      canAddBilling={state?.canAddBilling}
      onClose={() => {
        navigate('/settings/subscription', { replace: true });
      }}
      onSubmitSuccess={() => {
        void refetch();
        autoCloseWebView();
      }}
    />
  );
}

export function SettingsSubscriptionPage() {
  const { data: settings } = useCarrierSettings();
  const [{ utm_source, utm_medium, utm_content, driver_guid, back_url }] =
    useQuery();
  const {
    isLoading,
    isFetching,
    subscription,
    isDriverSeat,
    billableSeats,
    error: subscriptionError,
    refetchSubscriptionSettings,
  } = useSubscription();
  const navigate = useNavigate();
  const location = useLocation();
  const { action } = useParams();
  const { showLauncher, hideLauncher } = useCustomerSupport();
  const isEditCardDetailsDrawerOpen = action === 'edit_card_details';
  const isReactivatePlanDrawerOpen = action === 'reactivate_plan';
  const isSubscriptionContactEnabled = useFlag('ctms_subscription_contact');
  const carrierGuid = settings?.carrier.guid;
  const billingLocationState = location.state as
    | BillingLocationState
    | undefined;
  const pastDueLocationState = location.state as
    | PastDueCardDetailsLocationState
    | undefined;
  // Adding a billing address passes location state and keeps its own drawer.
  const isEditPaymentInfoAction =
    isEditCardDetailsDrawerOpen ||
    (action === 'edit_billing_address' && !billingLocationState) ||
    (isSubscriptionContactEnabled && action === 'edit_subscription_contact');
  const { data: taxPrice, isLoading: isPastDueLoading } = useEstimateTaxPrice(
    {
      seats_count: billableSeats?.billable_seats,
      include_total_invoice: false,
    },
    {
      enabled: isEditPaymentInfoAction && !!billableSeats?.billable_seats,
    },
  );
  const hasPastDue = !!taxPrice?.past_due_invoices?.length;
  const isPastDueCardDetailsDrawerOpen = isEditPaymentInfoAction && hasPastDue;
  // Waits for the past due check, so the regular drawer does not flash first.
  const canOpenEditDrawer = !isPastDueLoading && !hasPastDue;

  useErrorSnackbar(subscriptionError, {
    formatError: (error: Error) =>
      `Failed to fetch subscription details: ${error.message}`,
  });

  useEffect(() => {
    trackEvent('CTMS: Viewed Subscription Details Page', {
      utm_medium,
      carrier_guid: carrierGuid,
      driver_guid,
    });
    if (
      subscription?.subscription?.status === 'active' ||
      subscription?.subscription?.status === 'in_trial'
    ) {
      hideLauncher();
    } else {
      showLauncher();
    }
    return showLauncher;
  }, [
    hideLauncher,
    showLauncher,
    subscription?.subscription?.status,
    utm_medium,
    carrierGuid,
    driver_guid,
  ]);

  useEffect(() => {
    if (utm_source === 'email') {
      trackEvent('Carrier Clicked Upgrade Now', { utm_source });
    }
  }, [utm_source]);

  useEffect(() => {
    return () => {
      if (utm_content === 'Instant Transfer') {
        trackSubscriptionEvent({
          name: 'Carrier closed subscription detail page',
          utm_medium,
          utm_content: 'Instant Transfer',
          carrier_guid: carrierGuid,
          driver_guid,
        });
      }
    };
  }, [utm_medium, utm_content, carrierGuid, driver_guid]);

  return (
    <>
      <DocumentTitle title="Subscription Details - Settings" />

      <SubscriptionUpdateCardDetailsDrawerRoute
        hasCardHolderName={true}
        onSubmitSuccess={() => {
          void refetchSubscriptionSettings();
          autoCloseWebView();
        }}
        subscriptionDetails={subscription}
      />

      <SubscriptionUpdateCardDetailsDrawer
        open={isEditCardDetailsDrawerOpen && canOpenEditDrawer}
        title="Edit Card Details"
        hasCardHolderName={false}
        onClose={() => {
          navigate('/settings/subscription', { replace: true });
        }}
        onSubmitSuccess={() => {
          void refetchSubscriptionSettings();
        }}
        subscriptionDetails={subscription}
      />

      <SubscriptionUpgradePlanDrawerRoute
        refetch={() => {
          void refetchSubscriptionSettings();
        }}
        backUrl={back_url}
      />

      <SubscriptionReactivationDrawer
        open={isReactivatePlanDrawerOpen}
        onClose={() => {
          navigate(
            {
              pathname: '/settings/subscription',
              search: filterSearchParams({ utm_medium, driver_guid, back_url }),
            },
            { replace: true },
          );
        }}
        onSubmitSuccess={() => {
          autoCloseWebView();
        }}
      />

      <SubscriptionPastDueDrawer
        open={action === 'pay_past_due'}
        onClose={() => {
          navigate('/settings/subscription', { replace: true });
        }}
      />

      <SubscriptionPastDueCardDetailsDrawer
        open={isPastDueCardDetailsDrawerOpen}
        onClose={() => {
          navigate('/settings/subscription', { replace: true });
        }}
        onBack={
          pastDueLocationState?.isFromPastDueDrawer
            ? () => {
                navigate('/settings/subscription/pay_past_due', {
                  replace: true,
                });
              }
            : undefined
        }
      />

      <EditBillingAddress
        open={
          action === 'edit_billing_address' &&
          (!!billingLocationState || canOpenEditDrawer)
        }
      />

      <SubscriptionContactDrawer
        open={
          isSubscriptionContactEnabled &&
          action === 'edit_subscription_contact' &&
          canOpenEditDrawer
        }
        onClose={() => {
          navigate('/settings/subscription', { replace: true });
        }}
      />

      <SettingsPageLayout
        actions={
          !isDriverSeat && (
            <Link
              target="_blank"
              rel="noreferrer noopener"
              underline="hover"
              href="https://support.superdispatch.com/hc/articles/35612448514195-How-To-Manage-Your-Subscription-Plan-In-Carrier-TMS"
              onClick={(event: MouseEvent<HTMLAnchorElement>) => {
                if (isWebView) {
                  MobileAppBridge.openExternalUrl(event.currentTarget.href);
                }
              }}
            >
              <Typography color="primary" variant="body1">
                Learn More
              </Typography>
            </Link>
          )
        }
        title="Subscription Details"
        loading={isFetching}
      >
        <Box width="100%" maxWidth="950px">
          {isLoading && (
            <Stack space="small">
              <LoadingCard />
              <LoadingCard />
            </Stack>
          )}

          {!subscription && !isFetching && (
            <Card aria-label="No Subscription Details">
              <CardContent>
                <Typography variant="body1" gutterBottom={true}>
                  There are no subscription details.
                </Typography>
                <Typography gutterBottom={true}>
                  If you are in a trial period, subscription details will appear
                  after you subscribe.
                </Typography>
                If you are already a subscribed user, please contact support
              </CardContent>
            </Card>
          )}

          <Stack space="small">
            <SubscriptionCelebration />

            <SubscriptionPaymentAlerts />

            {subscription?.customer?.payment_method?.card?.masked_number ? (
              <SubscriptionDetailsCard />
            ) : (
              <SubscriptionPaymentForm />
            )}
          </Stack>
        </Box>
      </SettingsPageLayout>
    </>
  );
}
